import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppSignUpApi, SignUpAnswer } from "./AppSignUpApi";

// AppSignUpPost - the request half of `/claim`, `/pair` and `/dropbox`. Every decision is
// `AppSignUpApi`'s.
//
// ⚠ IT IS SEPARATE FROM THE RULES FOR THE REASON `AppEnrolPost` IS SEPARATE FROM `AppEnrol`: the
// Bridge suite imports the rules to cross-check both halves of the wire, and a combined file drags
// `XMLHttpRequest` into a Node typecheck - passing under tsx and failing under tsc.
//
// ⚠ WHY `XMLHttpRequest` AND NOT THE SHARED REST TRANSPORT (law 2, checked rather than guessed).
// `kSPRest.RestExecute` and `kBearerRestExecutor` are SharePoint-bound - a site url, a web to
// resolve, a SharePoint form digest and a shared waiting line - and none of that exists when
// posting to a container service. The same conclusion is recorded at the top of `AppSource.ts`.

export type SignUpPostDone = (Answer: SignUpAnswer) => void;

export class AppSignUpPost {
    private Url: string;
    private Body: string;
    private Method: string;
    private Done: SignUpPostDone;
    private Req: XMLHttpRequest | null = null;
    private Ended: boolean = false;
    private TimeoutMs: number;
    private Unknown: (() => void) | null;

    /** ⚠ An empty `Body` sends a GET - which is how `/dropbox` asks for the authorize URL. */
    public constructor(Url: string, Body: string, Done: SignUpPostDone, TimeoutMs: number = 0, Unknown: (() => void) | null = null) {
        this.Url = Url;
        this.Body = Body;
        this.Done = Done;
        this.TimeoutMs = TimeoutMs;
        this.Unknown = Unknown;
        this.Method = "POST";
        if (nz(Body).length === 0) { this.Method = "GET"; }
    }

    public Send = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Url.length === 0) {
                this.Finish(AppSignUpApi.No(AppSignUpApi.NoServiceWhy()));
                return;
            }
            this.Req = new XMLHttpRequest();
            (this.Req as XMLHttpRequest).onreadystatechange = this.OnState;
            (this.Req as XMLHttpRequest).open(this.Method, this.Url, true);
            (this.Req as XMLHttpRequest).timeout = this.TimeoutMs;
            (this.Req as XMLHttpRequest).ontimeout = this.OnUnknown;
            (this.Req as XMLHttpRequest).onerror = this.OnUnknown;
            if (this.Method === "POST") {
                (this.Req as XMLHttpRequest).setRequestHeader("Content-Type", "application/json");
                (this.Req as XMLHttpRequest).send(this.Body);
                return;
            }
            (this.Req as XMLHttpRequest).send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpPost.Send");
            this.Finish(AppSignUpApi.No(AppSignUpApi.OursWhy()));
        }
    };

    /**
     * ⛔ THE ONLY THING THAT ANSWERS, AND IT ANSWERS ONCE (the `PairRun.Finish` shape). A callback
     * that threw on the no-service sentence used to be answered AGAIN from `Send`'s catch with
     * "this is at our end" - two contradictory verdicts on one step of sign-up.
     */
    private Finish = (Answer: SignUpAnswer): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - this is the only thing that answers the
        // page, and the latch rather than the gate is what keeps that to once. A throwing callback
        // is reported, never answered a second time.
        try {
            if (this.Ended === true) { return; }
            this.Ended = true;
            this.Done(Answer);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpPost.Finish");
        }
    };

    /**
     * ⛔ THE BODY IS READ WHATEVER THE STATUS - see `AppSignUpApi`'s header. A transport that threw
     * on a non-2xx would replace the service's own explaining sentence with "request failed", in
     * the one flow a stranger meets first.
     */
    private OnState = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Req: XMLHttpRequest = this.Req as XMLHttpRequest;
            if (Req.readyState !== 4) { return; }
            if (Req.status === 0 && this.Unknown !== null) { this.OnUnknown(); return; }
            this.Finish(AppSignUpApi.Answer(Req.status, Req.responseText));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpPost.OnState");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: completion must release the waiting page even after the gate latches. */
    private OnUnknown = (): void => {
        try {
            if (this.Ended === true) { return; }
            if (this.Unknown === null) { this.Finish(AppSignUpApi.No("The connection did not answer. Please try again.")); return; }
            this.Ended = true;
            this.Unknown();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpPost.OnUnknown");
        }
    };
}
